// A14: Pearl's 4-candidate persona craft gallery. Captain types a role
// description, gets four candidate cards back, flips to inspect each
// one's rubric/anti-patterns, and picks one to materialise as a live
// agent (or watcher). Reroll regenerates; Branch forks variations
// along an axis.

import { useState } from "react";
import {
  ApiError,
  applyPersonaCandidate,
  clonePersonaFromCaptain,
  generatePersonaCandidates,
  type ModelBacking,
  type PersonaCandidate,
} from "../api/client";
import { UpsellCard, isPremiumRequired } from "./UpsellCard";

interface Props {
  workspaceId: number;
  orgId: number;
  defaultModel: string;
  onClose: () => void;
}

interface RawCandidate {
  candidate_id: number;
  card_json: Record<string, unknown>;
}

export function PersonaCandidateGallery({
  workspaceId,
  orgId,
  defaultModel,
  onClose,
}: Props) {
  const [role, setRole] = useState("");
  const [busy, setBusy] = useState<"generate" | "apply" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [premiumGated, setPremiumGated] = useState(false);
  const [candidates, setCandidates] = useState<RawCandidate[]>([]);
  const [flipped, setFlipped] = useState<Set<number>>(new Set());
  const [model, setModel] = useState(defaultModel);
  const [kind, setKind] = useState<"working" | "watcher">("working");
  // A27: persona creation is blocked at save time when no model_backing
  // is picked. Default to openrouter_key (one-click PKCE later, paste
  // for now) — the lowest-friction BYOK path. Captain can flip to
  // direct-provider keys, subscription, or premium pooled models.
  const [modelBacking, setModelBacking] =
    useState<ModelBacking>("openrouter_key");

  const generate = async (axis = "", parent = 0) => {
    if (!role.trim()) return;
    setBusy("generate");
    setError(null);
    try {
      const resp = await generatePersonaCandidates(
        orgId,
        role.trim(),
        axis,
        parent,
      );
      setCandidates(
        resp.candidates.map((c: PersonaCandidate) => ({
          candidate_id: c.candidate_id,
          card_json: (c.card_json ?? {}) as Record<string, unknown>,
        })),
      );
      setFlipped(new Set());
    } catch (e) {
      if (isPremiumRequired(e)) {
        setPremiumGated(true);
      } else {
        setError(e instanceof ApiError ? e.message : String(e));
      }
    } finally {
      setBusy(null);
    }
  };

  const cloneFromCaptain = async () => {
    setBusy("generate");
    setError(null);
    try {
      const resp = await clonePersonaFromCaptain(workspaceId);
      setCandidates(
        resp.candidates.map((c: PersonaCandidate) => ({
          candidate_id: c.candidate_id,
          card_json: (c.card_json ?? {}) as Record<string, unknown>,
        })),
      );
      setFlipped(new Set());
      void import("../observability/posthog").then((m) =>
        m.capture("persona_clone_from_captain", { workspace_id: workspaceId }),
      );
    } catch (e) {
      if (isPremiumRequired(e)) {
        setPremiumGated(true);
      } else {
        setError(e instanceof ApiError ? e.message : String(e));
      }
    } finally {
      setBusy(null);
    }
  };

  const reroll = () => void generate("");
  const branch = (idx: number) => {
    const axis = window.prompt(
      "Branch axis (e.g. 'more cautious', 'different voice', 'specialist on X'):",
    );
    const parent = candidates[idx]?.candidate_id ?? 0;
    if (axis !== null) void generate(axis, parent);
  };

  const apply = async (idx: number) => {
    const candidate = candidates[idx];
    if (!candidate || !candidate.candidate_id) {
      setError("missing candidate_id; reroll and try again");
      return;
    }
    setBusy("apply");
    setError(null);
    try {
      await applyPersonaCandidate(
        workspaceId,
        candidate.candidate_id,
        model,
        modelBacking,
        kind,
      );
      void import("../observability/posthog").then((m) =>
        m.capture("persona_candidate_applied", {
          workspace_id: workspaceId,
          candidate_id: candidate.candidate_id,
          model,
          model_backing: modelBacking,
          kind,
        }),
      );
      onClose();
    } catch (e) {
      if (isPremiumRequired(e)) {
        setPremiumGated(true);
      } else {
        setError(e instanceof ApiError ? e.message : String(e));
      }
    } finally {
      setBusy(null);
    }
  };

  const toggleFlip = (idx: number) => {
    setFlipped((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  if (premiumGated) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
        data-testid="persona-candidate-gallery"
      >
        <div className="flex w-full max-w-md flex-col gap-3 rounded-panel border border-edge bg-surface p-5 shadow-[var(--wt-shadow)]">
          <header className="flex items-baseline justify-between gap-4">
            <h2 className="text-sm font-semibold text-primary">
              Pearl — Persona Craft
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer text-xs text-muted hover:text-primary"
            >
              Close
            </button>
          </header>
          <UpsellCard
            feature="pearl-persona-craft"
            headline="Pearl is a premium feature"
            sub="Custom persona creation — design watchers and working agents tailored to your team — unlocks with premium. The default council watchers run on free tier."
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
      data-testid="persona-candidate-gallery"
    >
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col gap-4 overflow-y-auto rounded-panel border border-edge bg-surface p-5 shadow-[var(--wt-shadow)]">
        <header className="flex items-baseline justify-between gap-4">
          <h2 className="text-sm font-semibold text-primary">
            Pearl — pick a persona
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-[0.625rem] text-muted hover:text-primary"
          >
            Close
          </button>
        </header>

        <div className="flex items-end gap-2">
          <label className="flex flex-1 flex-col gap-1">
            <span className="text-xs font-medium text-muted">
              Describe the role
            </span>
            <input
              type="text"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void generate();
                }
              }}
              placeholder="e.g. backend engineer who reviews unsafe Rust blocks"
              className="rounded-md border border-edge bg-surface-raised px-2 py-1 text-xs text-primary placeholder:text-faint"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted">Kind</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as "working" | "watcher")}
              className="rounded-md border border-edge bg-surface-raised px-2 py-1 text-xs text-primary"
            >
              <option value="working">Working agent</option>
              <option value="watcher">Watcher</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted">Model</span>
            <input
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="anthropic/claude-sonnet-4.6"
              className="w-64 rounded-md border border-edge bg-surface-raised px-2 py-1 font-code text-xs text-primary"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted">
              Pays for tokens
            </span>
            <select
              value={modelBacking}
              onChange={(e) =>
                setModelBacking(e.target.value as ModelBacking)
              }
              className="rounded-md border border-edge bg-surface-raised px-2 py-1 text-xs text-primary"
              title="Which key funds this persona's calls. Staff (system agents) and BYOK (this persona) never cross stream — see Mission/pricing_and_tiers.md."
            >
              <option value="openrouter_key">OpenRouter (BYOK)</option>
              <option value="anthropic_key">Anthropic API (BYOK)</option>
              <option value="openai_key">OpenAI API (BYOK)</option>
              <option value="google_key">Google API (BYOK)</option>
              <option value="subscription">Subscription (Claude / Codex)</option>
              <option value="pooled">Pooled (Premium)</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() => void generate()}
            disabled={busy !== null || !role.trim()}
            className="rounded-md border border-accent bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent hover:bg-accent/20 disabled:opacity-50"
          >
            {busy === "generate" ? "Generating…" : "Generate 4"}
          </button>
          <button
            type="button"
            onClick={() => void cloneFromCaptain()}
            disabled={busy !== null}
            title="Pearl reads your recent decisions + chat and mirrors your working style into 4 candidate cards"
            className="rounded-md border border-edge px-3 py-1.5 text-xs font-medium text-muted hover:border-accent hover:text-accent disabled:opacity-50"
            data-testid="persona-clone-captain"
          >
            Clone my style
          </button>
          {candidates.length > 0 && (
            <button
              type="button"
              onClick={reroll}
              disabled={busy !== null}
              className="rounded-md border border-edge px-3 py-1.5 text-xs font-medium text-muted hover:border-accent hover:text-accent disabled:opacity-50"
            >
              Reroll
            </button>
          )}
        </div>

        {error && <p className="text-[0.625rem] text-danger">{error}</p>}

        {candidates.length === 0 && (
          <p className="text-xs text-faint">
            Type a role description and hit Generate. Pearl returns four
            candidate cards in the canonical 8-field shape — flip to see
            anti-patterns + rubric on the back.
          </p>
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {candidates.map((c, idx) => {
            const isFlipped = flipped.has(idx);
            const card = c.card_json;
            const display_name = (card.display_name as string) ?? `Candidate ${idx + 1}`;
            const role_title = (card.role_title as string) ?? "";
            const role_frame = (card.role_frame as string) ?? "";
            const heuristics = (card.core_heuristics as string[]) ?? [];
            const voice = (card.voice as string) ?? "";
            const anti = (card.anti_patterns as string[]) ?? [];
            const failure = (card.named_failure_modes as string[]) ?? [];
            const tools = (card.tool_discipline as string) ?? "";
            const rubric =
              (card.rubric as { axes?: Array<{ name: string; threshold: string }> })
                ?.axes ?? [];
            return (
              <article
                key={idx}
                className="flex flex-col gap-2 rounded-md border border-edge bg-surface-raised p-3"
              >
                <header className="flex items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold text-primary">
                    {display_name}
                  </h3>
                  <span className="text-[0.625rem] text-faint">
                    {role_title}
                  </span>
                </header>
                {!isFlipped ? (
                  <>
                    {role_frame && (
                      <p className="text-xs text-muted">{role_frame}</p>
                    )}
                    {heuristics.length > 0 && (
                      <ul className="flex flex-col gap-0.5 text-[0.625rem] text-muted">
                        {heuristics.slice(0, 5).map((h, i) => (
                          <li key={i}>• {h}</li>
                        ))}
                      </ul>
                    )}
                    {voice && (
                      <p className="text-[0.625rem] italic text-faint">
                        Voice: {voice}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    {anti.length > 0 && (
                      <div>
                        <p className="text-[0.625rem] font-medium text-muted">
                          Anti-patterns
                        </p>
                        <ul className="text-[0.625rem] text-muted">
                          {anti.map((a, i) => (
                            <li key={i}>• {a}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {failure.length > 0 && (
                      <div>
                        <p className="text-[0.625rem] font-medium text-muted">
                          Named failure modes
                        </p>
                        <ul className="text-[0.625rem] text-muted">
                          {failure.map((f, i) => (
                            <li key={i}>• {f}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {tools && (
                      <p className="text-[0.625rem] text-muted">
                        Tool discipline: {tools}
                      </p>
                    )}
                    {rubric.length > 0 && (
                      <div>
                        <p className="text-[0.625rem] font-medium text-muted">
                          Rubric (Shore grades against)
                        </p>
                        <ul className="text-[0.625rem] text-muted">
                          {rubric.map((r, i) => (
                            <li key={i}>
                              <span className="font-code">{r.name}</span> —{" "}
                              {r.threshold}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
                <div className="mt-auto flex items-center gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => toggleFlip(idx)}
                    className="rounded-md border border-edge px-2 py-1 text-[0.625rem] text-muted hover:border-accent hover:text-accent"
                  >
                    {isFlipped ? "Front" : "Flip"}
                  </button>
                  <button
                    type="button"
                    onClick={() => branch(idx)}
                    disabled={busy !== null}
                    className="rounded-md border border-edge px-2 py-1 text-[0.625rem] text-muted hover:border-accent hover:text-accent disabled:opacity-50"
                  >
                    Branch
                  </button>
                  <button
                    type="button"
                    onClick={() => void apply(idx)}
                    disabled={busy !== null || !model.trim()}
                    className="ml-auto rounded-md border border-accent bg-accent/10 px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/20 disabled:opacity-50"
                  >
                    {busy === "apply" ? "Applying…" : "Pick"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}
